import {
  clearActivityJournal,
  fetchActivityJournal,
  upsertActivityJournal,
  type ActivityJournalJobPayload,
} from "@/lib/api/activityJournal";

export type MailMutationPhase = "local" | "server-ok" | "imap-sync" | "done";

export type MailActivityKind = "mutation" | "receive" | "sync" | "send" | "other";

export type MailMutationProgress = {
  done: number;
  total: number;
};

export type MailMutationStepStatus = "pending" | "running" | "ok" | "error";

export type MailMutationStep = {
  id: string;
  label: string;
  status: MailMutationStepStatus;
  error?: string;
};

export type MailMutationJob = {
  id: string;
  label: string;
  phase: MailMutationPhase;
  kind?: MailActivityKind;
  error?: string;
  /** Erreur vue dans le journal — l’icône header redevient idle. */
  errorAcknowledged?: boolean;
  /** Conserver dans l’historique 7 j (audit), pas de dismiss rapide. */
  keepHistory?: boolean;
  createdAt: number;
  updatedAt: number;
  progress?: MailMutationProgress;
  steps?: MailMutationStep[];
};

export type MailMutationHeaderState = "idle" | "busy" | "error";

const SUCCESS_EPHEMERAL_MS = 90_000;
const MAX_HISTORY = 250;
const LEGACY_STORAGE_KEY = "mail_activity_journal_v1";
const dismissTimers = new Map<string, number>();
const jobs = new Map<string, MailMutationJob>();
const listeners = new Set<() => void>();
let persistTimer: number | null = null;
let hydrated = false;
let hydratePromise: Promise<void> | null = null;

function emit(): void {
  for (const cb of listeners) cb();
  schedulePersist();
}

function now(): number {
  return Date.now();
}

function newId(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${prefix}-${now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Lundi 00:00 locale de la semaine courante. */
export function startOfWeekMondayMs(ts: number = Date.now()): number {
  const d = new Date(ts);
  const day = d.getDay();
  const sinceMonday = day === 0 ? 6 : day - 1;
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - sinceMonday);
  return d.getTime();
}

function pruneByWeekAndCap(): void {
  const weekStart = startOfWeekMondayMs();
  for (const [id, job] of [...jobs.entries()]) {
    if (job.phase === "done" && job.createdAt < weekStart) {
      const timer = dismissTimers.get(id);
      if (timer) window.clearTimeout(timer);
      dismissTimers.delete(id);
      jobs.delete(id);
    }
  }

  const list = [...jobs.values()].sort((a, b) => b.createdAt - a.createdAt);
  if (list.length <= MAX_HISTORY) return;
  for (const job of list.slice(MAX_HISTORY)) {
    if (job.phase !== "done") continue;
    const timer = dismissTimers.get(job.id);
    if (timer) window.clearTimeout(timer);
    dismissTimers.delete(job.id);
    jobs.delete(job.id);
  }
}

function doneJobsForPersist(): MailMutationJob[] {
  return [...jobs.values()].filter((job) => {
    if (job.phase !== "done") return false;
    if (job.error) return true;
    if (job.keepHistory !== false) return true;
    return false;
  });
}

function normalizeServerJob(raw: ActivityJournalJobPayload): MailMutationJob | null {
  if (!raw?.id || typeof raw.createdAt !== "number") return null;
  const steps = raw.steps?.map((step) => ({
    id: String(step.id),
    label: String(step.label ?? ""),
    status: (["pending", "running", "ok", "error"].includes(step.status)
      ? step.status
      : "ok") as MailMutationStepStatus,
    error: step.error,
  }));
  return {
    id: raw.id,
    label: String(raw.label ?? ""),
    phase: "done",
    kind: (raw.kind as MailActivityKind | undefined) ?? "mutation",
    error: raw.error,
    errorAcknowledged: raw.errorAcknowledged,
    keepHistory: raw.keepHistory !== false,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt ?? raw.createdAt,
    progress: raw.progress,
    steps,
  };
}

function mergeDoneJob(job: MailMutationJob): void {
  if (!job?.id || typeof job.createdAt !== "number") return;
  const weekStart = startOfWeekMondayMs();
  if (job.createdAt < weekStart) return;

  const existing = jobs.get(job.id);
  if (existing && existing.phase !== "done") {
    // Job local encore en cours : ne pas écraser.
    return;
  }
  if (existing && existing.updatedAt > (job.updatedAt ?? 0)) {
    return;
  }

  jobs.set(job.id, {
    ...job,
    phase: "done",
    keepHistory: job.keepHistory !== false,
  });
}

function readLegacyLocalJobs(): MailMutationJob[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as {
      jobs?: MailMutationJob[];
      weekStart?: number;
    };
    const weekStart = startOfWeekMondayMs();
    if (parsed.weekStart != null && parsed.weekStart < weekStart) {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      return [];
    }
    const out: MailMutationJob[] = [];
    for (const job of parsed.jobs ?? []) {
      if (!job?.id || typeof job.createdAt !== "number") continue;
      if (job.createdAt < weekStart) continue;
      const wasInFlight = job.phase !== "done" && !job.error;
      out.push({
        ...job,
        phase: "done",
        label: wasInFlight
          ? `${String(job.label).replace(/\s*[….]*$/, "")} (interrompu)`
          : job.label,
        keepHistory: job.keepHistory !== false,
      });
    }
    return out;
  } catch {
    return [];
  }
}

function scheduleSuccessDismiss(id: string): void {
  const job = jobs.get(id);
  if (!job || job.keepHistory) return;

  const prev = dismissTimers.get(id);
  if (prev) window.clearTimeout(prev);
  dismissTimers.set(
    id,
    window.setTimeout(() => {
      dismissTimers.delete(id);
      const current = jobs.get(id);
      if (!current || current.error || current.keepHistory) return;
      if (current.phase === "done") {
        jobs.delete(id);
        emit();
      }
    }, SUCCESS_EPHEMERAL_MS)
  );
}

function schedulePersist(): void {
  if (typeof window === "undefined") return;
  if (persistTimer != null) return;
  persistTimer = window.setTimeout(() => {
    persistTimer = null;
    void persistJournal();
  }, 600);
}

async function persistJournal(): Promise<void> {
  if (typeof window === "undefined") return;
  pruneByWeekAndCap();
  const toStore = doneJobsForPersist();
  if (toStore.length === 0) return;
  try {
    await upsertActivityJournal(toStore);
  } catch {
    /* réseau */
  }
}

async function hydrateJournalFromServer(): Promise<void> {
  if (typeof window === "undefined") return;

  const legacy = readLegacyLocalJobs();
  for (const job of legacy) {
    mergeDoneJob(job);
  }

  const res = await fetchActivityJournal();
  if (res.ok) {
    for (const raw of res.data.jobs ?? []) {
      const job = normalizeServerJob(raw);
      if (job) mergeDoneJob(job);
    }
    pruneByWeekAndCap();

    if (legacy.length > 0) {
      try {
        await upsertActivityJournal(legacy.filter((j) => j.phase === "done"));
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      } catch {
        /* garde LS pour retry */
      }
    } else {
      try {
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
  } else if (legacy.length > 0) {
    // API KO : garder l’historique LS en mémoire pour la session.
    pruneByWeekAndCap();
  }
}

function hydrateJournal(): void {
  if (hydrated || typeof window === "undefined") return;
  hydrated = true;
  pruneByWeekAndCap();
  if (hydratePromise != null) return;
  hydratePromise = hydrateJournalFromServer()
    .catch(() => {
      /* ignore */
    })
    .finally(() => {
      for (const cb of listeners) cb();
    });
}

/** Force le chargement BDD (appel UI au mount). */
export function ensureMailActivityJournalHydrated(): void {
  hydrateJournal();
}

export function subscribeMailMutationProgress(callback: () => void): () => void {
  hydrateJournal();
  listeners.add(callback);
  return () => listeners.delete(callback);
}

export function getActiveMailMutationJobs(): MailMutationJob[] {
  hydrateJournal();
  pruneByWeekAndCap();
  return [...jobs.values()].sort((a, b) => b.updatedAt - a.updatedAt || b.createdAt - a.createdAt);
}

/** Alias journal — historique semaine (lundi → dimanche). */
export function getMailMutationJournal(): MailMutationJob[] {
  return getActiveMailMutationJobs();
}

export function getLatestMailMutationJob(): MailMutationJob | null {
  const list = getActiveMailMutationJobs();
  const inFlight = list.filter((job) => job.phase !== "done");
  if (inFlight.length > 0) return inFlight[0];
  return list[0] ?? null;
}

export function hasVisibleMailMutationJob(): boolean {
  return getLatestMailMutationJob() != null;
}

export function hasActiveMailMutation(): boolean {
  for (const job of jobs.values()) {
    if (job.phase !== "done") return true;
  }
  return false;
}

/**
 * Mutations qui doivent pauser IMAP / sync.
 * Exclut `receive` (journal observationnel) — sinon deadlock :
 * prep attend le corps → corps attend IMAP → IMAP pausé car prep active.
 */
export function hasBlockingMailMutation(): boolean {
  for (const job of jobs.values()) {
    if (job.phase === "done") continue;
    // Journal observationnel — ne doit jamais pauser IMAP.
    if (job.kind === "receive" || job.kind === "other" || job.kind === "sync") continue;
    return true;
  }
  return false;
}

export function hasUnresolvedMailMutationError(): boolean {
  for (const job of jobs.values()) {
    if (job.error && !job.errorAcknowledged) return true;
  }
  return false;
}

export function getMailMutationHeaderState(): MailMutationHeaderState {
  if (hasUnresolvedMailMutationError()) return "error";
  if (hasActiveMailMutation()) return "busy";
  return "idle";
}

export function beginMailMutation(
  label: string,
  totalOrOptions?:
    | number
    | {
        total?: number;
        stepLabels?: string[];
        kind?: MailActivityKind;
        keepHistory?: boolean;
      }
): string {
  hydrateJournal();
  const options =
    typeof totalOrOptions === "number"
      ? { total: totalOrOptions }
      : totalOrOptions ?? {};
  const total = options.total;
  const stepLabels = options.stepLabels;
  const kind = options.kind ?? "mutation";
  const keepHistory = options.keepHistory !== false;
  const id = newId("mut");
  const steps =
    stepLabels && stepLabels.length > 0
      ? stepLabels.map((stepLabel, index) => ({
          id: `${id}-step-${index}`,
          label: stepLabel.trim() || `Élément ${index + 1}`,
          status: "pending" as const,
        }))
      : undefined;

  jobs.set(id, {
    id,
    label,
    kind,
    keepHistory,
    phase: "local",
    createdAt: now(),
    updatedAt: now(),
    progress:
      total != null && total > 0
        ? { done: 0, total }
        : steps
          ? { done: 0, total: steps.length }
          : undefined,
    steps,
  });
  pruneByWeekAndCap();
  emit();
  return id;
}

export function setMailMutationLabel(id: string, label: string): void {
  const job = jobs.get(id);
  if (!job) return;
  const next = label.trim();
  if (!next || next === job.label) return;
  jobs.set(id, { ...job, label: next, updatedAt: now() });
  emit();
}

export function setMailMutationProgress(id: string, done: number, total: number): void {
  const job = jobs.get(id);
  if (!job) return;
  const safeTotal = Math.max(1, total);
  jobs.set(id, {
    ...job,
    updatedAt: now(),
    progress: { done: Math.min(Math.max(0, done), safeTotal), total: safeTotal },
  });
  emit();
}

export function setMailMutationPhase(id: string, phase: MailMutationPhase): void {
  const job = jobs.get(id);
  if (!job) return;
  jobs.set(id, { ...job, phase, updatedAt: now() });
  emit();
}

export function setMailMutationStepStatus(
  id: string,
  stepIndex: number,
  status: MailMutationStepStatus,
  error?: string
): void {
  const job = jobs.get(id);
  if (!job?.steps || stepIndex < 0 || stepIndex >= job.steps.length) return;
  const steps = job.steps.map((step, index) =>
    index === stepIndex
      ? { ...step, status, error: status === "error" ? error : undefined }
      : step
  );
  const done = steps.filter((step) => step.status === "ok" || step.status === "error").length;
  jobs.set(id, {
    ...job,
    steps,
    updatedAt: now(),
    progress: { done, total: steps.length },
  });
  emit();
}

export function setMailMutationStepLabel(
  id: string,
  stepIndex: number,
  label: string
): void {
  const job = jobs.get(id);
  if (!job?.steps || stepIndex < 0 || stepIndex >= job.steps.length) return;
  const next = label.trim();
  if (!next || next === job.steps[stepIndex]?.label) return;
  const steps = job.steps.map((step, index) =>
    index === stepIndex ? { ...step, label: next } : step
  );
  jobs.set(id, { ...job, steps, updatedAt: now() });
  emit();
}

export function finishMailMutation(id: string): void {
  const job = jobs.get(id);
  if (!job) return;
  const steps = job.steps?.map((step) =>
    step.status === "pending" || step.status === "running" ? { ...step, status: "ok" as const } : step
  );
  jobs.set(id, {
    ...job,
    phase: "done",
    updatedAt: now(),
    steps,
    progress: steps
      ? { done: steps.length, total: steps.length }
      : job.progress
        ? { done: job.progress.total, total: job.progress.total }
        : job.progress,
  });
  emit();
  scheduleSuccessDismiss(id);
}

export function failMailMutation(id: string, error: string): void {
  const job = jobs.get(id);
  if (!job) return;
  const timer = dismissTimers.get(id);
  if (timer) window.clearTimeout(timer);
  dismissTimers.delete(id);

  let steps = job.steps;
  if (steps) {
    const firstPending = steps.findIndex(
      (step) => step.status === "pending" || step.status === "running"
    );
    if (firstPending >= 0) {
      steps = steps.map((step, index) =>
        index === firstPending ? { ...step, status: "error" as const, error } : step
      );
    }
  }

  jobs.set(id, {
    ...job,
    phase: "done",
    error,
    errorAcknowledged: false,
    keepHistory: true,
    updatedAt: now(),
    steps,
  });
  emit();
}

export function acknowledgeMailMutationErrors(): void {
  let changed = false;
  for (const [id, job] of jobs) {
    if (job.error && !job.errorAcknowledged) {
      jobs.set(id, { ...job, errorAcknowledged: true, updatedAt: now() });
      changed = true;
    }
  }
  if (changed) emit();
}

export function clearFinishedMailMutations(): void {
  void import("@/lib/mail/mailActivityJournalBridge")
    .then((m) => {
      m.abortMailActivityPrepareJob();
    })
    .catch(() => {
      /* ignore */
    });

  for (const [id, job] of [...jobs.entries()]) {
    const isReceivePrep = job.kind === "receive";
    if (job.phase !== "done" && !isReceivePrep) continue;
    const timer = dismissTimers.get(id);
    if (timer) window.clearTimeout(timer);
    dismissTimers.delete(id);
    jobs.delete(id);
  }
  for (const cb of listeners) cb();
  void clearActivityJournal().catch(() => {
    /* ignore */
  });
}

export function cancelMailMutation(id: string): void {
  const timer = dismissTimers.get(id);
  if (timer) window.clearTimeout(timer);
  dismissTimers.delete(id);
  jobs.delete(id);
  emit();
}
